'use client';
// Client-Modul: Kontext und Hooks laufen nur im Browser bzw. im SSR-Rahmen der
// Seite. Server-Code (Routen, Strukturdaten) importiert reine Helfer aus Modulen,
// die auch diese Hooks anbieten (`shared/anzahl.ts`) — mit der Markierung ist
// das erlaubt, die Hooks bleiben dort Verweise (SEO-Paket F7, Abbildungen).
import { useCallback, useEffect, useState } from 'react';
import { useLanguage } from './useLanguage';

// Cache für Übersetzungen (pro Sprache): entweder das Translations-Objekt
// oder — während des Ladens — das laufende Promise. In-Flight-Sharing
// verhindert, dass N gleichzeitig gemountete Komponenten N Requests feuern.
const translationCache = {};
// Separates Map für die "In-Flight"-Promises pro Sprache.
// Sobald die Antwort da ist, wird der Eintrag gelöscht und das Ergebnis
// landet im translationCache.
const translationInFlight = {};

/**
 * Legt das Bündel einer Sprache vor, bevor eine Server-Seite (Next.js-Phase 2)
 * rendert — auf dem Server und im Browser vor dem Übernehmen der Seite.
 *
 * `useTranslation` liest seinen Startwert synchron aus diesem Cache. Liegt das
 * Bündel dort, rendern Server und Browser dieselben Texte; fehlt es, stünden
 * auf dem Server die deutschen Vorgaben und im Browser die übersetzten Texte,
 * und React verwürfe das Server-HTML. Überschrieben wird immer: Auf dem Server
 * lebt dieses Modul über viele Anfragen, und eine Textänderung der Verwaltung
 * soll dort ankommen (der Abruf davor hält sich selbst kurz vor).
 */
export function legeUebersetzungenVor(language, data) {
  translationCache[language] = data;
}

// --- Verwerfen nach einer Änderung im Admin --------------------------------
// Der Cache liegt im Modul, der gerenderte Stand aber in jedem `useState` der
// gemounteten Hooks. Nur den Cache-Eintrag zu löschen erneuert deshalb gar
// nichts: die Instanzen halten ihre alte Kopie, und der Lade-Effekt hängt an
// `language` — der läuft nicht neu, solange die Sprache dieselbe bleibt.
//
// Deshalb ein Zählwerk plus Abonnenten: jeder Hook hängt darin und lässt
// seinen Lade-Effekt erneut laufen, sobald jemand verwirft. Der Redis-Cache
// des Backends wird beim Schreiben ohnehin verworfen (`database.py`), der
// erneute Abruf holt also wirklich den neuen Text.
const translationSubscribers = new Set();
let translationGeneration = 0;

/**
 * Verwirft die geladenen Übersetzungen und lässt jeden gemounteten Hook neu
 * laden. Ohne `language` fliegt der gesamte Cache raus.
 *
 * Aufrufen, wo Übersetzungs-TEXTE geschrieben werden (Admin-Editor,
 * Neuübersetzung, Sprach-Launch) — sonst zeigt die laufende Sitzung bis zum
 * nächsten harten Neuladen den alten Text.
 */
export function invalidateTranslations(language) {
  if (language) {
    delete translationCache[language];
    delete translationInFlight[language];
  } else {
    Object.keys(translationCache).forEach((k) => delete translationCache[k]);
    Object.keys(translationInFlight).forEach((k) => delete translationInFlight[k]);
  }
  translationGeneration += 1;
  translationSubscribers.forEach((fn) => {
    try {
      fn(translationGeneration);
    } catch (e) {
      console.error('[i18n] Abonnent des Datenstands hat geworfen:', e);
    }
  });
}

// --- Ladefehler sichtbar machen (globale Regeln #5 und #7) -----------------
// Scheitert `/api/i18n/translations/<sprache>`, rendert die Anwendung überall
// die deutschen Default-Texte aus `t(key, default)` — in JEDER Sprache. Für
// einen englischen Nutzer sieht das nach einer schlechten Übersetzung aus,
// nicht nach einem Ausfall. Ohne Meldung fällt das niemandem auf, erst recht
// nicht mehr, seit der Endpunkt dank Redis-Cache und ETag (PR #273) im
// Normalfall unmerklich schnell antwortet.
//
// Deshalb: jeder Fehlschlag landet in der Konsole UND weckt die Abonnenten,
// aus denen der <TranslationLoadErrorBanner/> im App-Root seinen Hinweis baut.
const translationLoadErrorSubscribers = new Set();
let lastTranslationLoadError = null;

function notifyTranslationLoadError() {
  translationLoadErrorSubscribers.forEach((fn) => {
    try {
      fn(lastTranslationLoadError);
    } catch (e) {
      console.error('[i18n] Fehler-Abonnent hat geworfen:', e);
    }
  });
}

function reportTranslationLoadError(language, reason) {
  console.error(
    `[i18n] Übersetzungen für "${language}" konnten nicht geladen werden: ${reason} ` +
    '— die Anwendung zeigt jetzt die deutschen Default-Texte.'
  );
  lastTranslationLoadError = { language, reason, at: Date.now() };
  notifyTranslationLoadError();
}

function clearTranslationLoadError() {
  if (!lastTranslationLoadError) return;
  lastTranslationLoadError = null;
  notifyTranslationLoadError();
}

/** Letzter Ladefehler, oder `null` wenn zuletzt alles geklappt hat. */
export function getTranslationLoadError() {
  return lastTranslationLoadError;
}

/** Abonniert Ladefehler; gibt die Abmelde-Funktion zurück. */
export function subscribeTranslationLoadError(fn) {
  translationLoadErrorSubscribers.add(fn);
  return () => translationLoadErrorSubscribers.delete(fn);
}

// Set für bereits gemeldete Keys (verhindert doppelte Reports pro Session)
const reportedKeys = new Set();

// Queue für fehlende Keys (werden gesammelt und batch-weise ans Backend gesendet)
let missingKeysQueue = [];
let flushTimeout = null;

const DEFERRED_TEXT = Symbol.for('paperball.deferredText');

/**
 * Merker für einen Text, der erst BEIM RENDERN übersetzt wird.
 *
 * Warum es das braucht: Übersetzungen kommen asynchron aus `/api/i18n/...`.
 * Wer `t()` beim Mount aufruft (Effekt, der eine Fehlermeldung in den State
 * schreibt), bekommt zuverlässig den deutschen Default-Text — und der bleibt
 * dort für immer stehen, weil der Effekt nicht neu läuft, wenn die
 * Übersetzungen eintreffen. Für alle nicht-deutschen Sprachen ist das ein
 * Pivot-Fallback (globale Regel #7 verbietet die), sichtbar z. B. als
 * deutscher OAuth-Fehler auf /gb/english/login.
 *
 * Deshalb: im State den KEY halten, nicht den Text.
 *
 *   setError(deferredText('auth.oauth_error_denied', 'Die Anmeldung wurde abgebrochen.'))
 *   ...
 *   <span>{resolveText(error)}</span>
 *
 * `resolveText` reicht fertige Strings unverändert durch — Aufrufer, die
 * ohnehin erst nach dem Laden übersetzen (Submit-Handler) oder eine
 * Backend-Meldung anzeigen, bleiben also unverändert.
 */
export function deferredText(key, defaultText, variables) {
  return { [DEFERRED_TEXT]: true, key, defaultText, variables };
}

export function isDeferredText(value) {
  return !!value && typeof value === 'object' && value[DEFERRED_TEXT] === true;
}

/**
 * Hook für Übersetzungen mit automatischer Key-Registrierung.
 * 
 * Usage:
 * const { t } = useTranslation();
 * 
 * // Einfacher Text
 * t('nav.home', 'Startseite')
 * 
 * // Mit Platzhaltern
 * t('welcome.message', 'Hallo {name}!', { name: 'Max' })
 * 
 * // Mit Kontext für Übersetzer
 * t('button.save', 'Speichern', {}, { context: 'Button im Formular' })
 */
export function useTranslation() {
  const { language } = useLanguage();
  const [translations, setTranslations] = useState(translationCache[language] || {});
  const [loading, setLoading] = useState(!translationCache[language]);
  // Ladefehler kommt aus dem Modul-State, damit jeder Mount denselben Stand
  // sieht — auch der, der sich nur an ein laufendes Promise gehängt hat.
  const [loadError, setLoadError] = useState(getTranslationLoadError);
  useEffect(() => subscribeTranslationLoadError(setLoadError), []);
  // Zählwerk des Datenstands: steigt bei `invalidateTranslations()` und zieht
  // damit den Lade-Effekt unten erneut durch — auch bei gleicher Sprache.
  const [generation, setGeneration] = useState(translationGeneration);
  useEffect(() => {
    translationSubscribers.add(setGeneration);
    // Zwischen `useState` und diesem Effekt kann bereits verworfen worden
    // sein; dann fehlte dem Hook die Runde. Einmal nachziehen.
    setGeneration(translationGeneration);
    return () => translationSubscribers.delete(setGeneration);
  }, []);

  // Übersetzungen laden — mit Request-Deduplication über `translationInFlight`.
  // Wenn bereits eine Anfrage läuft, hängen wir uns an das existierende Promise.
  useEffect(() => {
    const loadTranslations = async () => {
      // 1. Schon gecacht?
      if (translationCache[language]) {
        setTranslations(translationCache[language]);
        setLoading(false);
        return;
      }

      // 2. Bereits eine Request in-flight? Dann das gleiche Promise nutzen.
      if (translationInFlight[language]) {
        try {
          const data = await translationInFlight[language];
          setTranslations(data);
        } catch (e) {
          // Heute nicht erreichbar — das geteilte Promise fängt in seinem
          // eigenen `.catch` alles ab und resolved mit `{}`. Falls sich das
          // ändert, wird der Fehler gemeldet statt geschluckt (Regel #5); den
          // Hinweis am Bildschirm hat der auslösende Mount ohnehin schon
          // gesetzt.
          reportTranslationLoadError(language, `geteiltes Promise: ${e?.message || String(e)}`);
        } finally {
          setLoading(false);
        }
        return;
      }

      // 3. Eigener Fetch — merke das Promise damit parallele Mounts nicht
      //    nochmal fetchen.
      const standBeimStart = translationGeneration;
      const promise = fetch(`/api/i18n/translations/${language}`)
        .then(async (r) => {
          // Ein 304 taucht hier nicht auf: die Anfrage trägt kein eigenes
          // `If-None-Match`, der Browser löst die Revalidierung selbst auf
          // und reicht den gecachten 200er durch.
          if (!r.ok) {
            const rumpf = await r.text().catch(() => '');
            throw new Error(
              `HTTP ${r.status}${r.statusText ? ` ${r.statusText}` : ''}` +
              (rumpf ? ` — ${rumpf.slice(0, 200)}` : '')
            );
          }
          const data = await r.json();
          const t = data.translations || data || {};
          // Wurde während des Abrufs verworfen, ist diese Antwort schon
          // wieder veraltet: dann nicht cachen, sonst käme der alte Stand
          // durch die Hintertür zurück. Der erneute Lauf holt ihn frisch.
          if (standBeimStart === translationGeneration) {
            translationCache[language] = t;
          }
          clearTranslationLoadError();
          return t;
        })
        .catch(e => {
          // Fängt beide Richtungen ab: den Netzwerk-/CORS-Abbruch des `fetch`
          // und den oben geworfenen HTTP- bzw. JSON-Fehler. Der leere Rumpf
          // bleibt als Rückgabe nötig, damit `t()` weiterrendert — aber er
          // wird bewusst NICHT in `translationCache` gelegt: sonst würde ein
          // einmaliger 500er die Sitzung dauerhaft auf die deutschen Defaults
          // festnageln, weil jeder weitere Mount den vergifteten Cache-Treffer
          // nimmt. So versucht es der nächste Mount erneut.
          reportTranslationLoadError(language, e?.message || String(e));
          return {};
        })
        .finally(() => {
          // Promise aus der In-Flight-Map entfernen, damit ein neuer Mount
          // nach späterer Sprachänderung wieder einen echten Fetch triggern kann.
          // Nur, wenn der Eintrag noch DIESER Abruf ist: hat `invalidateTranslations`
          // zwischenzeitlich einen neuen gestartet, würde das Löschen ihn
          // gleich wieder aus der Deduplizierung reißen.
          if (translationInFlight[language] === promise) {
            delete translationInFlight[language];
          }
        });
      translationInFlight[language] = promise;

      try {
        const t = await promise;
        setTranslations(t);
      } finally {
        setLoading(false);
      }
    };

    loadTranslations();
  }, [language, generation]);

  // Fehlende Keys ans Backend senden (debounced)
  // Funktioniert in allen Umgebungen (Dev + Production),
  // damit neue t()-Keys immer automatisch registriert werden.
  const reportMissingKey = useCallback((key, defaultText, context) => {
    // Bereits in dieser Session gemeldet? → Skip
    if (reportedKeys.has(key)) {
      return;
    }

    // Prüfen ob Key schon in Queue
    if (missingKeysQueue.some(item => item.key === key)) {
      return;
    }

    reportedKeys.add(key);
    missingKeysQueue.push({
      key,
      default_text: defaultText,
      context: context,
      category: key.split('.')[0], // Erste Teil des Keys als Kategorie
    });

    // Debounced senden (nach 2 Sekunden Inaktivität)
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    flushTimeout = setTimeout(flushMissingKeys, 2000);
  }, []);

  // Translate-Funktion
  const t = useCallback((key, defaultText = key, variables = {}, options = {}) => {
    // Übersetzung aus Cache holen
    let text = translations[key];

    // Fallback auf Default-Text wenn nicht gefunden
    if (!text) {
      text = defaultText;

      // Fehlenden Key melden (nur wenn Default-Text angegeben). Während die
      // Übersetzungen noch laden, ist JEDER Key „fehlend" — das wäre kein
      // Befund, sondern nur Lärm auf /api/i18n/report-missing-keys. Nach dem
      // Laden rendert die Komponente erneut (`t` haengt an `translations`),
      // wirklich fehlende Keys werden also weiterhin gemeldet.
      if (defaultText !== key && !loading) {
        reportMissingKey(key, defaultText, options.context);
      }
    }

    // Variablen ersetzen: {name} -> Wert. Genau diese Schreibweise, mit EINER
    // Klammer — `{{name}}` bliebe als `{Wert}` stehen. Der Ersatz geht über eine
    // Funktion: Als String gäbe `replace` einem `$&` oder `$1` im Wert
    // (Schlüsselname, Webhook-Adresse) eine Sonderbedeutung.
    if (variables && Object.keys(variables).length > 0) {
      Object.entries(variables).forEach(([varName, value]) => {
        text = text.replace(new RegExp(`\\{${varName}\\}`, 'g'), () => String(value));
      });
    }

    return text;
  }, [translations, reportMissingKey, loading]);

  // Löst einen mit `deferredText()` geparkten Text auf. Fertige Strings
  // (und leere Werte) kommen unveraendert zurueck.
  // Ein Platzhalter darf selbst erst beim Rendern entstehen: eine Funktion
  // bekommt das aktuelle `t` (so formuliert `grundBeimRendern` den Grund eines
  // Fehlers in der Sprache, die beim Anzeigen gilt, nicht beim Fangen).
  const resolveText = useCallback((value) => {
    if (!isDeferredText(value)) return value;
    const variablen = {};
    Object.entries(value.variables || {}).forEach(([name, wert]) => {
      variablen[name] = typeof wert === 'function' ? wert(t) : wert;
    });
    return t(value.key, value.defaultText, variablen);
  }, [t]);

  // Prüfen ob Key existiert
  const hasTranslation = useCallback((key) => {
    return !!translations[key];
  }, [translations]);

  // Verwirft die Übersetzungen der AKTUELLEN Sprache und lädt sie neu.
  // Wer eine andere Sprache verwirft (Admin-Editor arbeitet an einer Sprache,
  // die nicht seine Oberflächensprache sein muss), nimmt direkt
  // `invalidateTranslations(code)`.
  //
  // `loading` bleibt bewusst `false`: die alten Texte stehen während des
  // erneuten Abrufs weiter im State, es ist also nichts zu warten — und ein
  // `true` würde nebenbei die Meldung fehlender Keys aussetzen.
  const invalidateCache = useCallback(() => {
    invalidateTranslations(language);
  }, [language]);

  return {
    t,
    resolveText,
    language,
    loading,
    // `{ language, reason, at }` wenn der letzte Ladeversuch scheiterte —
    // sonst `null`. Der globale Hinweis dazu hängt im App-Root.
    loadError,
    hasTranslation,
    invalidateCache,
    translations,
  };
}

// Fehlende Keys ans Backend senden
async function flushMissingKeys() {
  if (missingKeysQueue.length === 0) return;

  const keysToSend = [...missingKeysQueue];
  missingKeysQueue = [];

  try {
    await fetch('/api/i18n/report-missing-keys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys: keysToSend }),
    });
  } catch (error) {
    // Bei Fehler: Keys nicht erneut melden (reportedKeys Set verhindert Endlos-Retry)
    if (process.env.NODE_ENV !== 'production') {
      console.error('[i18n] Failed to report missing keys:', error);
    }
  }
}

export default useTranslation;
